import { UTCDate } from '@date-fns/utc';
import type { SegmentsListFilters } from '@openx/components-segment/types.ts';
import type { ByPhraseFilter, ByValueFilter } from '@openx/components-table/serverSideCriteriaProcessing.ts';
import { expiringSoonStatus } from '@openx/types/segments.ts';
import type { AllTableCriteria } from '@openx/types/tableCriteria.ts';
import { parseMultiValueFilter } from '@openx/utils/lib/multiValueFilter.ts';
import { addDays } from 'date-fns';
import omit from 'lodash/omit';
import { type MappedNumericFilter, mapNumericFilter } from './mapNumericFilter.ts';
import { type ParsedDate, parseDateToISO } from './parseDateToISO.ts';

const numericFields = new Set(['price_cpm', 'price_pom', 'reach']);
const serverFieldNameByFilter: Record<string, string> = {
  price_cpm: 'computed_price_cpm',
  price_pom: 'computed_price_pom',
};
const dateFields = new Set(['created_date', 'updated_date']);
const fieldsToDecode = new Set([
  'segment_category',
  'segment_sub_category',
  'segment_type',
  'provider',
  'account',
  'regions',
  'countries',
]);

export const adjustCriteriaFilters = (criteria: AllTableCriteria<SegmentsListFilters>) => {
  const hasExpiringSoonStatus = criteria.filters?.status?.includes?.(expiringSoonStatus);
  const now = new UTCDate();
  now.setSeconds(0, 0);

  const initialByValue: ByValueFilter = hasExpiringSoonStatus
    ? [{ field: 'expiration_date', values: [now.toISOString(), addDays(now, 10).toISOString()] }]
    : [];

  const byValue: ByValueFilter = Object.entries(omit(criteria.filters, 'phrase')).reduce((acc, [field, values]) => {
    const isNumericField = numericFields.has(field);

    if (isNumericField && values) {
      const mappedValues: MappedNumericFilter = mapNumericFilter(values);

      acc.push({
        field: serverFieldNameByFilter[field] ?? field,
        values: mappedValues,
      });

      return acc;
    }

    if (dateFields.has(field) && values) {
      const [min, max]: ParsedDate = parseDateToISO(values);

      acc.push({
        field,
        values: [min, max],
      });

      return acc;
    }

    if (fieldsToDecode.has(field) && values) {
      acc.push({
        field,
        values: parseMultiValueFilter(values),
      });

      return acc;
    }

    if (values) {
      acc.push({
        field,
        values: values.split(',').filter(status => !!status && status !== expiringSoonStatus),
      });
    }

    return acc;
  }, initialByValue);

  const byPhrase: ByPhraseFilter = {
    fields: ['name', 'segment_category', 'segment_sub_category', 'segment_type', 'description', 'account', 'provider'],
    phrase: criteria.filters?.phrase ?? '',
  };

  return { byPhrase, byValue };
};
